// Account page — the user's REAL details (name + phone). These are required
// before the first property-files extraction: the portal form is filled with
// the real name/phone, and the real email goes in the on-behalf note in the
// form body. Email comes from Google (read-only); name/phone are editable and
// stored in users/{uid} (owner-only per Firestore rules).
import { auth, isGuest, signOut, markUserInitiatedSignOut } from '../lib/firebase.js';
import {
  watchMyDetails,
  saveMyDetails,
  watchAttributes,
  ensureDefaultAttributes,
  defineAttribute,
  renameAttribute,
  retypeAttribute,
  deleteAttribute,
  watchAccountShare,
  createAccountShare,
  revokeAccountShare,
  setAccountShareActive,
  removeAccountMember,
  accountMemberNames,
} from '../lib/firestore.js';
import { renderHeaderIdentity, escapeHtml, escapeAttr } from './parts.js';
import { normalizeName, displayLabel, NUMBER, STRING } from '../lib/attrs.js';

export function renderAccount(el, { user, navigate, query = {} }) {
  if (isGuest(user)) {
    el.innerHTML = `
      <div class="app-header">
        <a class="brand" href="#/map">Property Source</a>
        <nav><a href="#/map">Map</a></nav>
        <div id="header-who"></div>
      </div>
      <main>
        <div class="card">
          <h1>Account</h1>
          <p class="muted">Sign in to manage your details. Your name, phone, and email are used when requesting property files on your behalf.</p>
          <a class="btn" href="#/login">Sign in with Google</a>
        </div>
      </main>`;
    return;
  }

  el.innerHTML = `
    <div class="app-header">
      <a class="brand" href="#/map">Property Source</a>
      <nav><a href="#/map">Map</a></nav>
      <div id="header-who"></div>
    </div>
    <main>
      <div class="card">
        <h1>Your details</h1>
        <p class="muted">Used when requesting property files on your behalf — the document portal receives your real name and phone, and a note with your email.</p>
        <form id="details-form" style="display:flex;flex-direction:column;gap:10px;max-width:420px;margin-top:14px">
          <label>
            <span>Full name</span>
            <input type="text" id="d-name" autocomplete="name" placeholder="Jane Doe" />
          </label>
          <label>
            <span>Phone</span>
            <input type="tel" id="d-phone" autocomplete="tel" placeholder="021 123 4567" />
          </label>
          <label>
            <span>Email (from Google — shown to the portal in the request note)</span>
            <input type="email" id="d-email" value="${escapeAttr(user.email || '')}" readonly disabled />
          </label>
          <div style="display:flex;align-items:center;gap:10px">
            <button class="btn" id="d-save" type="submit">Save</button>
            <span class="muted" id="d-hint"></span>
          </div>
        </form>
        ${query.next ? `<p class="muted" style="margin-top:10px"><a href="#/${escapeAttr(query.next)}">← Back</a></p>` : ''}
      </div>
      <div class="card">
        <h2>My attributes</h2>
        <p class="muted">Your attribute definitions power the recommendations and filters across your properties — they don't restrict what you can store. String values suggest the common ones you've used.</p>
        <div id="my-attrs"><div class="muted">Loading…</div></div>
      </div>
      <div class="card">
        <h2>Portfolio sharing</h2>
        <p class="muted">Connect your whole portfolio with other Property Source accounts — you'll see each other's properties (past and future), and either side can stop sharing at any time.</p>
        <div id="portfolio-share"><div class="muted">Loading…</div></div>
      </div>
      <div style="margin-top:20px;border-top:1px solid var(--border);padding-top:16px">
        <button class="btn ghost danger" id="sign-out">Sign out</button>
      </div>
    </main>`;

  renderHeaderIdentity(el.querySelector('#header-who'), { user, navigate });

  // Sign-out moved here from the header (compact header = email links to this
  // page). The flag makes onAuthStateChanged land on login, not a fresh guest.
  el.querySelector('#sign-out').addEventListener('click', () => {
    markUserInitiatedSignOut();
    signOut(auth);
  });

  const nameEl = el.querySelector('#d-name');
  const phoneEl = el.querySelector('#d-phone');
  const hintEl = el.querySelector('#d-hint');
  const form = el.querySelector('#details-form');

  const unsub = watchMyDetails(user.uid, (details) => {
    if (!details) return; // keep Google defaults until the user saves
    if (details.display_name) nameEl.value = details.display_name;
    if (details.phone) phoneEl.value = details.phone;
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = nameEl.value.trim();
    const phone = phoneEl.value.trim();
    if (!name || !phone) {
      hintEl.textContent = 'Name and phone are both required.';
      return;
    }
    const btn = el.querySelector('#d-save');
    btn.disabled = true;
    try {
      await saveMyDetails(user.uid, { display_name: name, phone });
      hintEl.textContent = 'Saved ✓';
      if (query.next) {
        navigate(`#/${query.next}`);
        return;
      }
    } catch (err) {
      console.warn('save details failed', err);
      hintEl.textContent = 'Save failed — try again.';
    }
    btn.disabled = false;
  });

  // ── attribute definitions manager (docs/plans/user-attributes.md) ──────
  let defs = [];
  let renaming = null;   // name being renamed
  let focusId = null;    // element to focus after render
  let newName = '';
  let newType = STRING;
  const attrsEl = el.querySelector('#my-attrs');

  const PEN_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>`;
  const hint = (t) => {
    const h = attrsEl.querySelector('#attrs-hint');
    if (h) h.textContent = t;
  };

  function renderAttrs() {
    attrsEl.innerHTML = `
      ${defs.length ? `<div class="attr-rows">${defs.map((d) => {
        const ren = renaming === d.id;
        return `<div class="attr-row">
          ${ren
            ? `<input type="text" id="rename-input" class="attr-value-input" value="${escapeAttr(displayLabel(d.id))}" maxlength="64" aria-label="New name" />`
            : `<span class="attr-name">${escapeHtml(displayLabel(d.id))} <span class="attr-type">${d.type}</span></span>`}
          <select class="attr-retype" data-name="${escapeAttr(d.id)}" aria-label="Type of ${escapeAttr(displayLabel(d.id))}">
            <option value="text" ${d.type === STRING ? 'selected' : ''}>Text</option>
            <option value="number" ${d.type === NUMBER ? 'selected' : ''}>Number</option>
          </select>
          ${ren
            ? `<button class="pen" id="rename-done" aria-label="Done renaming">${PEN_ICON}</button>`
            : `<button class="pen" data-rename="${escapeAttr(d.id)}" aria-label="Rename ${escapeAttr(displayLabel(d.id))}">${PEN_ICON}</button>`}
          <button class="attr-del" data-del="${escapeAttr(d.id)}" aria-label="Delete ${escapeAttr(displayLabel(d.id))}">×</button>
        </div>`;
      }).join('')}</div>` : '<p class="muted">No attributes yet — add one below, or define them on a property.</p>'}
      <form id="attrs-add" class="attr-add" style="margin-top:12px">
        <input type="text" id="new-attr-name" placeholder="Attribute name" value="${escapeAttr(newName)}" maxlength="64" aria-label="Attribute name" />
        <span class="attr-type-toggle">
          <label><input type="radio" name="new-attr-type" value="text" ${newType === STRING ? 'checked' : ''}/> Text</label>
          <label><input type="radio" name="new-attr-type" value="number" ${newType === NUMBER ? 'checked' : ''}/> Number</label>
        </span>
        <button class="btn" type="submit">Add</button>
      </form>
      <p class="muted" id="attrs-hint"></p>`;

    attrsEl.querySelectorAll('.attr-retype').forEach((s) =>
      s.addEventListener('change', () =>
        retypeAttribute(s.dataset.name, s.value === 'number' ? NUMBER : STRING).catch(() => hint('Failed — try again')),
      ),
    );
    attrsEl.querySelectorAll('.attr-del').forEach((b) =>
      b.addEventListener('click', () => deleteAttribute(b.dataset.del).catch(() => hint('Failed — try again'))),
    );
    attrsEl.querySelectorAll('.pen[data-rename]').forEach((b) =>
      b.addEventListener('click', () => {
        renaming = b.dataset.rename;
        focusId = 'rename-input';
        renderAttrs();
      }),
    );
    const renameInput = attrsEl.querySelector('#rename-input');
    if (renameInput) {
      const commit = async () => {
        const oldName = renaming;
        renaming = null;
        try {
          await renameAttribute(oldName, renameInput.value);
          hint('Renamed ✓');
        } catch (err) {
          console.warn('rename failed', err);
          hint('Rename failed');
        }
        renderAttrs();
      };
      renameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') {
          renaming = null;
          renderAttrs();
        }
      });
      attrsEl.querySelector('#rename-done').addEventListener('click', commit);
    }
    attrsEl.querySelector('#new-attr-name').addEventListener('input', (e) => (newName = e.target.value));
    attrsEl.querySelectorAll('input[name="new-attr-type"]').forEach((r) =>
      r.addEventListener('change', () => {
        newType = attrsEl.querySelector('input[name="new-attr-type"]:checked').value === 'number' ? NUMBER : STRING;
      }),
    );
    attrsEl.querySelector('#attrs-add').addEventListener('submit', async (e) => {
      e.preventDefault();
      const n = normalizeName(newName);
      if (!n) {
        hint('Enter a valid name (no /, ≤ 64 chars).');
        return;
      }
      try {
        await defineAttribute(n, newType);
        newName = '';
        hint('Added ✓');
        renderAttrs();
      } catch (err) {
        console.warn('define failed', err);
        hint('Add failed — try again');
      }
    });
    if (focusId) {
      const f = attrsEl.querySelector('#' + focusId);
      if (f) {
        f.focus();
        f.select();
      }
      focusId = null;
    }
  }

  ensureDefaultAttributes(user.uid).catch(() => {});
  const unsubAttrs = watchAttributes(user.uid, (next) => {
    defs = next;
    renderAttrs();
  });

  // ── portfolio sharing card (docs/plans/account-sharing.md, S2) ────────
  // Live from account_shares/{uid}: no profile → create link; active → link
  // row + linked accounts list + revoke; inactive → re-enable. The link doc
  // itself is public (token = key); access is the materialized member docs.
  const shareEl = el.querySelector('#portfolio-share');
  let acc = null;
  let shareNames = {}; // member uid → display name (for the linked list)

  const shareLink = (token) => `${location.origin}/#/account-share/${token}`;

  function renderShareCard() {
    const hint = () => shareEl.querySelector('#share-hint');
    if (!acc || !acc.link_token) {
      shareEl.innerHTML = `
        <p class="muted">No share link yet — create one to invite other accounts into your portfolio. Anyone with the link can connect their account to yours.</p>
        <button class="btn" id="create-share">Create share link</button>
        <p class="muted" id="share-hint"></p>`;
      shareEl.querySelector('#create-share').addEventListener('click', async () => {
        const btn = shareEl.querySelector('#create-share');
        btn.disabled = true;
        try {
          await createAccountShare(user.uid);
          hint().textContent = 'Link created ✓';
        } catch (e) {
          console.warn('create share failed', e);
          hint().textContent = 'Could not create the link — try again.';
          btn.disabled = false;
        }
      });
      return;
    }
    if (!acc.active) {
      shareEl.innerHTML = `
        <p class="error">Sharing is off — your link is dead and linked accounts lost access to your portfolio (and you to theirs).</p>
        <button class="btn" id="reenable-share">Re-enable sharing</button>
        <p class="muted" id="share-hint"></p>`;
      shareEl.querySelector('#reenable-share').addEventListener('click', async () => {
        const btn = shareEl.querySelector('#reenable-share');
        btn.disabled = true;
        try {
          await setAccountShareActive(user.uid, true);
          hint().textContent = 'Sharing is on again ✓';
        } catch (e) {
          console.warn('re-enable failed', e);
          hint().textContent = 'Could not re-enable — try again.';
          btn.disabled = false;
        }
      });
      return;
    }
    const link = shareLink(acc.link_token);
    const members = (acc.member_uids || []).map((m) => ({
      uid: m,
      name: shareNames[m] || 'Guest',
      guest: !shareNames[m],
    }));
    shareEl.innerHTML = `
      <div style="display:flex;gap:8px;align-items:center;margin:10px 0">
        <input type="text" id="share-link" readonly value="${escapeAttr(link)}" aria-label="Share link" style="flex:1" />
        <button class="btn ghost" id="copy-link">Copy</button>
      </div>
      <p class="muted" id="share-hint"></p>
      <h2 style="margin-top:16px">Linked accounts</h2>
      ${members.length
        ? `<div>${members.map((m) => `
          <div class="member-item">
            <span>${escapeHtml(m.name)}${m.guest ? ' <span class="muted">(guest)</span>' : ''}</span>
            <span class="spacer"></span>
            <button class="remove-member" data-uid="${escapeAttr(m.uid)}" title="Remove linked account — cuts sharing both ways" aria-label="Remove ${escapeHtml(m.name)}">×</button>
          </div>`).join('')}</div>`
        : '<p class="muted">No linked accounts yet — share your link.</p>'}
      <div style="margin-top:14px">
        <button class="btn ghost danger" id="revoke-share">Revoke sharing</button>
      </div>`;
    shareEl.querySelector('#copy-link').addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(link);
        hint().textContent = 'Copied ✓';
      } catch {
        hint().textContent = 'Copy blocked — select the link manually.';
      }
    });
    shareEl.querySelector('#revoke-share').addEventListener('click', async () => {
      if (!confirm('Revoke sharing? Linked accounts immediately lose access to your portfolio (and you to theirs).')) return;
      const btn = shareEl.querySelector('#revoke-share');
      btn.disabled = true;
      try {
        await revokeAccountShare(user.uid);
        hint().textContent = 'Sharing revoked.';
      } catch (e) {
        console.warn('revoke failed', e);
        hint().textContent = 'Could not revoke — try again.';
        btn.disabled = false;
      }
    });
    shareEl.querySelectorAll('.remove-member').forEach((b) =>
      b.addEventListener('click', () => {
        const m = members.find((x) => x.uid === b.dataset.uid);
        if (!confirm(`Remove ${m ? m.name : 'this account'}? Sharing is cut both ways — they lose your portfolio and you lose theirs.`)) return;
        removeAccountMember(user.uid, b.dataset.uid).catch((e) => {
          console.warn('remove member failed', e);
          hint().textContent = 'Could not remove — try again.';
        });
      }),
    );
  }

  const unsubShare = watchAccountShare(user.uid, async (next) => {
    acc = next;
    if (acc?.active && acc.member_uids?.length) {
      shareNames = await accountMemberNames(acc.member_uids);
    } else {
      shareNames = {};
    }
    renderShareCard();
  });

  return () => {
    unsub();
    unsubAttrs();
    unsubShare();
  };
}
